// FILE: src/components/QuestionFactory/AutoPublishSettings.js
//
// Phase 3 — per-cert auto-publish toggle. When enabled, items at
// publish_recommendation='auto_publish' (overall quality 90+) bypass
// the admin staging queue and are promoted straight into the live
// questions table by the edge function.
//
// Default OFF for every cert. Confirm dialog on flip ON so an admin
// doesn't accidentally start shipping straight to learners.

import React, { useCallback, useEffect, useState } from 'react';
import { getAllExams } from '../../config/exams';
import {
  getAutoPublishSettings,
  setAutoPublishSetting,
} from '../../services/questionFactoryService';

const STYLES = {
  card: {
    background: '#15152a', border: '1px solid #2a2a4a', borderRadius: '8px',
    padding: '16px 20px', marginBottom: '24px',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  title:  { fontSize: '0.95rem', fontWeight: 600, color: '#eee' },
  hint:   { fontSize: '0.78rem', color: '#aab', lineHeight: 1.5, marginBottom: '12px' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between',
         padding: '8px 12px', borderRadius: '4px', marginBottom: '6px',
         background: '#0f0f1e', border: '1px solid #2a2a4a' },
  cert: { fontWeight: 600, color: '#eee', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.82rem' },
  state: { fontSize: '0.75rem', color: '#aab', marginLeft: '8px' },
  toggle: {
    background: '#3a3a6e', border: '1px solid #5a5a9e', color: '#fff',
    padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem',
  },
  toggleOn: {
    background: '#2e5a3a', border: '1px solid #4a9a5a',
  },
  busy: { opacity: 0.55, cursor: 'wait' },
  error: { color: '#ff6b6b', fontSize: '0.8rem', marginTop: '6px' },
};

// Config-driven: every active (feature-enabled) cert appears here, so new certs
// like PMI-PMOCP show up automatically. Auto-publish state for each comes from
// the get_auto_publish_settings RPC (rows seeded per cert).
const CERTS = getAllExams().map((e) => ({ id: e.examId, label: e.displayName }));

export default function AutoPublishSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading]   = useState(true);
  const [error,   setError]     = useState(null);
  const [busyId,  setBusyId]    = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const rows = await getAutoPublishSettings();
      const map = {};
      for (const r of rows) map[r.exam_id] = !!r.auto_publish_enabled;
      setSettings(map);
    } catch (e) { setError(e.message || String(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const flip = useCallback(async (examId, enabled) => {
    if (enabled) {
      if (!window.confirm(
        `Turn auto-publish ON for ${examId.toUpperCase()}?\n\n` +
        `New items reaching publish_recommendation = auto_publish (overall quality 90+) ` +
        `will be promoted straight into the live questions table without admin review. ` +
        `You can flip this back off any time.`
      )) return;
    }
    setBusyId(examId); setError(null);
    try {
      await setAutoPublishSetting({ examId, enabled });
      setSettings((s) => ({ ...s, [examId]: enabled }));
    } catch (e) { setError(e.message || String(e)); }
    finally { setBusyId(null); }
  }, []);

  return (
    <div style={STYLES.card} data-testid="qf-auto-publish-settings">
      <div style={STYLES.header}>
        <div style={STYLES.title}>Auto-publish settings</div>
      </div>
      <div style={STYLES.hint}>
        When ON, items reaching <code>publish_recommendation = auto_publish</code>{' '}
        (overall quality 90+) bypass the admin review queue and are promoted
        straight into the live <code>questions</code> table. Default OFF.
      </div>
      {loading && <div style={{ color: '#888', fontSize: '0.85rem' }}>Loading…</div>}
      {error && <div style={STYLES.error}>{error}</div>}
      {!loading && CERTS.map((c) => {
        const enabled = !!settings[c.id];
        const isBusy = busyId === c.id;
        return (
          <div key={c.id} style={STYLES.row}>
            <div>
              <span style={STYLES.cert}>{c.label}</span>
              <span style={STYLES.state}>{enabled ? 'auto-publish ON' : 'auto-publish OFF'}</span>
            </div>
            <button data-testid={`qf-auto-toggle-${c.id}`}
                    style={isBusy
                      ? { ...STYLES.toggle, ...(enabled ? STYLES.toggleOn : {}), ...STYLES.busy }
                      : { ...STYLES.toggle, ...(enabled ? STYLES.toggleOn : {}) }}
                    disabled={isBusy}
                    onClick={() => flip(c.id, !enabled)}>
              {isBusy ? 'Saving…' : enabled ? 'Turn OFF' : 'Turn ON'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
